import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Layout from '../Layout';
import Questionnaire from '../Questionnaire';
import {
  enregistrerSinistre, listerGaranties, lireDossier, lireQuestionnaire,
  lireSinistre, majDossier, relireDeclaration,
} from '../api';
import ChampDate from '../../components/ChampDate/ChampDate';

const PAYS = [
  { code: 'BE', libelle: 'Belgique' },
  { code: 'LU', libelle: 'Luxembourg' },
  { code: 'FR', libelle: 'France' },
];

/**
 * Première étape : le récit du sinistre par l'assuré.
 *
 * On lui demande de raconter avec ses mots avant toute question technique :
 * c'est ce texte qui orientera ensuite le choix des postes de réparation.
 */
export default function Declaration({ expertiseId, onSuivant, onEtape }) {
  const [champs, setChamps] = useState({
    declaration: '', dateClaims: '', adress: '', country: 'BE',
    tva_recuperable: null, nature: '', reference: '',
  });
  // Nom et référence vivent sur le dossier, pas sur le sinistre : ils servent
  // à le retrouver dans une liste, pas à décrire ce qui s'est passé.
  const [dossier, setDossier] = useState({ name: '', reference: '' });
  const [natures, setNatures] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [reponses, setReponses] = useState({});
  const [garanties, setGaranties] = useState([]);
  const [choisies, setChoisies] = useState([]);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [relecture, setRelecture] = useState(null);
  const [relectureEnCours, setRelectureEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const [sinistre, listeGaranties, fiche] = await Promise.all([
          lireSinistre(expertiseId),
          listerGaranties(),
          lireDossier(expertiseId).catch(() => null),
        ]);
        if (!vivant) return;
        if (fiche) {
          setDossier({
            // « Mon sinistre » est le nom posé à l'ouverture : c'est un
            // gabarit, pas un choix. On le laisse vide pour inviter à nommer.
            name: fiche.name === 'Mon sinistre' ? '' : (fiche.name || ''),
            reference: fiche.reference || '',
          });
        }
        setChamps({
          declaration: sinistre.declaration || '',
          reference: sinistre.reference || '',
          dateClaims: sinistre.dateClaims || '',
          adress: sinistre.adress || '',
          country: sinistre.country || 'BE',
          tva_recuperable: sinistre.tva_recuperable,
        });
        setChoisies(sinistre.guarantees || []);
        setGaranties(listeGaranties);
        setReponses(sinistre.reponses || {});
        setNatures((await lireQuestionnaire()).natures);
      } catch {
        if (vivant) setErreur("Impossible de charger votre dossier.");
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    return () => { vivant = false; };
  }, [expertiseId]);

  // Le questionnaire suit la nature : changer de nature change les questions.
  useEffect(() => {
    let vivant = true;
    if (!champs.nature) { setQuestionnaire(null); return undefined; }
    lireQuestionnaire(champs.nature)
      .then((d) => vivant && setQuestionnaire(d))
      .catch(() => vivant && setQuestionnaire(null));
    return () => { vivant = false; };
  }, [champs.nature]);

  const modifier = (nom) => (e) =>
    setChamps((c) => ({ ...c, [nom]: e.target.value }));

  const choisirNature = (e) => {
    // Les réponses d'une autre nature n'ont plus de sens : on repart à neuf
    // plutôt que de garder des réponses orphelines dans le dossier.
    setChamps((c) => ({ ...c, nature: e.target.value }));
    setReponses({});
  };

  const demanderRelecture = async () => {
    setRelectureEnCours(true);
    setErreur('');
    try {
      // Le récit doit être en base avant d'être relu, sinon on relit l'ancien.
      await enregistrerSinistre(expertiseId, {
        ...champs, dateClaims: champs.dateClaims || null, reponses,
        guarantees_ids: choisies.map((g) => g.id),
      });
      setRelecture(await relireDeclaration(expertiseId));
    } catch (e) {
      setErreur(e?.response?.data?.detail
        || "La relecture n'a pas abouti. Réessayez dans un instant.");
    } finally {
      setRelectureEnCours(false);
    }
  };

  const valider = async (sansAvancer = false) => {
    if (!champs.declaration.trim()) {
      setErreur("Racontez ce qui s'est passé : c'est le point de départ du dossier.");
      return;
    }
    setEnregistrement(true);
    setErreur('');
    try {
      await enregistrerSinistre(expertiseId, {
        ...champs,
        dateClaims: champs.dateClaims || null,
        reponses,
        guarantees_ids: choisies.map((g) => g.id),
      });
      await majDossier(expertiseId, {
        name: dossier.name.trim() || 'Mon sinistre',
        reference: dossier.reference.trim(),
      });
      if (sansAvancer !== true) onSuivant();
    } catch {
      setErreur("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <Layout
      etape="sinistre"
      onEtape={onEtape}
      titre="Que s'est-il passé ?"
      introduction="Racontez avec vos mots. Inutile d'employer un vocabulaire technique — c'est ce récit qui nous servira de point de départ."
      actions={
        <>
          {/* Le délai de déclaration court dès le sinistre : on doit pouvoir
              l'envoyer avant de connaître la cause et avant tout devis. */}
          <Button onClick={async () => { await valider(true);
                                         onEtape?.('declaration-sinistre'); }}
                  disabled={chargement || enregistrement
                            || !champs.declaration.trim()}>
            Déclarer maintenant à mon assureur
          </Button>
          <Button variant="contained" size="large" onClick={valider}
                  disabled={chargement || enregistrement}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Nom de ce dossier" value={dossier.name}
              onChange={(e) => setDossier((d) => ({ ...d, name: e.target.value }))}
              disabled={chargement} fullWidth
              placeholder="Dégât des eaux du salon"
              helperText="Facultatif — pour le retrouver dans votre liste."
            />
            <TextField
              label="Votre référence interne" value={dossier.reference}
              onChange={(e) => setDossier((d) => ({
                ...d, reference: e.target.value }))}
              disabled={chargement} fullWidth
              helperText="Facultatif — votre propre numéro de dossier, si vous en tenez un."
            />
          </Stack>

          <FormControl disabled={chargement}>
            <FormLabel sx={{ mb: 1 }}>
              <Typography variant="body1" component="span"
                          sx={{ fontWeight: 600 }}>
                Qu'est-il arrivé ?
              </Typography>
            </FormLabel>
            <RadioGroup value={champs.nature} onChange={choisirNature}>
              {natures.map((n) => (
                <FormControlLabel key={n.code} value={n.code}
                                  control={<Radio size="small" />}
                                  label={n.libelle} />
              ))}
            </RadioGroup>
          </FormControl>

          {questionnaire?.questions?.length > 0 && (
            <Questionnaire
              questions={questionnaire.questions}
              reponses={reponses}
              onReponse={(code, valeur) =>
                setReponses((r) => ({ ...r, [code]: valeur }))}
              desactive={chargement}
            />
          )}

          <TextField
            label="Racontez ce qui s'est passé"
            placeholder="Par exemple : de l'eau coule depuis le plafond de ma cuisine, la salle de bain se trouve juste au-dessus."
            multiline minRows={4}
            value={champs.declaration} onChange={modifier('declaration')}
            disabled={chargement}
            helperText="Précisez ce que vous avez constaté, et où."
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <ChampDate
              label="Quand l'avez-vous constaté ?"
              InputLabelProps={{ shrink: true }}
              value={champs.dateClaims} onChange={modifier('dateClaims')}
              disabled={chargement}
            />
            <TextField
              select label="Pays" value={champs.country}
              onChange={modifier('country')} disabled={chargement}
              helperText="Détermine les prix de référence applicables."
            >
              {PAYS.map((p) => (
                <MenuItem key={p.code} value={p.code}>{p.libelle}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Adresse du sinistre" value={champs.adress}
            onChange={modifier('adress')} disabled={chargement}
          />

          {/* Elle n'existe qu'après l'ouverture du sinistre par la compagnie :
              on la demande sans l'exiger, et elle rejoint ensuite l'objet de
              chaque courriel. */}
          <TextField
            label="Référence du sinistre chez votre assureur"
            value={champs.reference} onChange={modifier('reference')}
            disabled={chargement}
            helperText="Elle vous sera communiquée à l'ouverture du dossier. Laissez vide si vous ne l'avez pas encore."
          />

          <Autocomplete
            multiple options={garanties} value={choisies}
            onChange={(e, valeur) => setChoisies(valeur)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            disabled={chargement}
            renderTags={(valeur, getTagProps) =>
              valeur.map((option, index) => (
                <Chip label={option.name} size="small"
                      {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Garanties concernées"
                helperText="Si vous ne savez pas, laissez vide : nous le déterminerons avec votre contrat." />
            )}
          />

          <Stack spacing={2}>
            <Stack direction="row">
              <Button variant="outlined" onClick={demanderRelecture}
                      disabled={chargement || relectureEnCours
                                || !champs.declaration.trim()}>
                {relectureEnCours ? 'Relecture en cours…' : 'Relire ma déclaration'}
              </Button>
            </Stack>

            {relecture?.contradictions?.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Deux choses ne concordent pas
                </Typography>
                {relecture.contradictions.map((c, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    Vous écrivez « {c.dans_le_recit} », mais vous avez répondu
                    « {c.dans_le_questionnaire} ».
                  </Typography>
                ))}
                <Typography variant="body2" color="text.secondary">
                  Ce n'est pas forcément une erreur — corrigez si besoin.
                </Typography>
              </Alert>
            )}

            {relecture?.manques?.length > 0 && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Ces précisions vous seront demandées
                </Typography>
                {relecture.manques.map((m, i) => (
                  <Typography key={i} variant="body2">· {m.question}</Typography>
                ))}
              </Alert>
            )}

            {relecture?.reformulation
              && relecture.reformulation !== champs.declaration && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small"
                          onClick={() => setChamps((c) => ({
                            ...c, declaration: relecture.reformulation }))}>
                    Utiliser
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Une formulation plus claire
                </Typography>
                <Typography variant="body2">{relecture.reformulation}</Typography>
              </Alert>
            )}

            {relecture && !relecture.contradictions?.length
              && !relecture.manques?.length && (
              <Alert severity="success">
                Votre déclaration est complète et cohérente.
              </Alert>
            )}
          </Stack>

          {questionnaire?.pieces?.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Ce qu'il faudra réunir
              </Typography>
              {questionnaire.pieces.map((p) => (
                <Typography key={p.libelle} variant="body2">
                  · {p.libelle}
                  {p.obligatoire && ' — indispensable'}
                  {p.aide && ` (${p.aide})`}
                </Typography>
              ))}
            </Alert>
          )}

          <FormControl>
            <FormLabel sx={{ mb: 1 }}>
              <Typography variant="body2" component="span">
                Récupérez-vous la TVA ?
              </Typography>
            </FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Un particulier ne la récupère pas ; une société le plus souvent oui.
              La réponse change la base sur laquelle votre indemnité se calcule.
            </Typography>
            <RadioGroup
              row
              value={champs.tva_recuperable === null ? '' : String(champs.tva_recuperable)}
              onChange={(e) =>
                setChamps((c) => ({ ...c, tva_recuperable: e.target.value === 'true' }))
              }
            >
              <FormControlLabel value="false" control={<Radio />} label="Non, je suis un particulier" />
              <FormControlLabel value="true" control={<Radio />} label="Oui" />
            </RadioGroup>
          </FormControl>
        </Stack>
      </Paper>
    </Layout>
  );
}
